import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Tooltip,
  Paper,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  CameraAlt,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Coffee,
  LocationOn,
  Refresh,
  CheckCircle,
  Cancel,
  Warning,
  Home,
  Business,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { attendanceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CoffeeIcon from '@mui/icons-material/Coffee';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VoiceAnnouncements from '../components/VoiceAssistant';
import FaceDetectionCamera from '../components/FaceDetectionCamera';

const MotionCard = motion(Card);

const Attendance = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [breakStatus, setBreakStatus] = useState(null);
  const [workType, setWorkType] = useState(user?.work_type || 'wfo');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState('login');
  const [location, setLocation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [faceVerificationStatus, setFaceVerificationStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadTodayAttendance();
    loadBreakStatus();
    getLocation();
  }, []);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (err) => {
          console.log('Location error:', err);
        }
      );
    }
  };

  const loadTodayAttendance = async () => {
    try {
      const response = await attendanceAPI.getToday();
      if (response.data.success) {
        setAttendance(response.data.data);
        if (response.data.data.attendance?.login_status) {
          setFaceVerificationStatus(response.data.data.attendance.login_status);
        }
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const loadBreakStatus = async () => {
    try {
      const response = await attendanceAPI.getBreakStatus();
      if (response.data.success) {
        setBreakStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error loading break status:', error);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await Promise.all([loadTodayAttendance(), loadBreakStatus()]);
    setIsRefreshing(false);
    setSuccess('Data refreshed successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const getRealTimeBreakTime = () => {
    if (!attendance?.attendance?.breaks) return 0;
    
    let totalMinutes = 0;
    attendance.attendance.breaks.forEach(brk => {
      if (brk.status === 'active') {
        const startTime = new Date(brk.start_time);
        const elapsed = Math.floor((currentTime - startTime) / 60000);
        totalMinutes += elapsed;
      } else if (brk.duration) {
        totalMinutes += brk.duration;
      }
    });
    return totalMinutes;
  };

  const getRealTimeWorkingTime = () => {
    if (!attendance?.attendance?.login_time) return 0;
    
    const loginTime = new Date(attendance.attendance.login_time);
    const logoutTime = attendance.attendance.logout_time ? new Date(attendance.attendance.logout_time) : currentTime;
    
    let totalMinutes = Math.floor((logoutTime - loginTime) / 60000);
    const breakTime = getRealTimeBreakTime();
    totalMinutes = Math.max(0, totalMinutes - breakTime);
    
    return totalMinutes;
  };

  // Initialize voice assistant on user interaction
  const [voiceInitialized, setVoiceInitialized] = useState(false);

  // Initialize voice when page loads or user interacts
  const initVoice = async () => {
    if (!voiceInitialized) {
      try {
        await VoiceAnnouncements.init();
        setVoiceInitialized(true);
      } catch (e) {
        console.log('[VOICE] Init error:', e);
      }
    }
  };

  // Announce voice when camera dialog opens
  useEffect(() => {
    if (showCamera) {
      initVoice();
      // Speak face verification instruction after a short delay
      const timer = setTimeout(() => {
        VoiceAnnouncements.faceInstruction().catch(() => {});
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showCamera]);

  const handleLogin = async (selfieImage) => {
    setLoading(true);
    setError('');
    try {
      const image = selfieImage;
      const formData = new FormData();
      formData.append('work_type', workType);
      formData.append('location', location ? `${location.latitude}, ${location.longitude}` : '');
      formData.append('latitude', location?.latitude || '');
      formData.append('longitude', location?.longitude || '');
      
      if (image) {
        formData.append('selfie_base64', image);
        const blob = await fetch(image).then(r => r.blob());
        formData.append('selfie', blob, 'selfie.jpg');
      }

      const response = await attendanceAPI.checkInFace(formData);
      if (response.data.success) {
        setSuccess(response.data.message);
        const faceVerified = response.data.data?.face_verified;
        const employeeName = response.data.data?.employee_name || user?.name || 'Employee';
        const loginTime = response.data.data?.attendance?.login_time;
        
        if (faceVerified) {
          setFaceVerificationStatus('verified');
          VoiceAnnouncements.loginSuccess(employeeName, loginTime).catch(() => {});
        } else {
          VoiceAnnouncements.loginSuccessPending(employeeName, loginTime).catch(() => {});
        }
        loadTodayAttendance();
        loadBreakStatus();
        setShowCamera(false);
      } else {
        setError(response.data.message);
        VoiceAnnouncements.error(response.data.message).catch(() => {});
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Login failed';
      setError(errMsg);
      VoiceAnnouncements.error(errMsg).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (selfieImage) => {
    setLoading(true);
    setError('');
    try {
      const image = selfieImage;
      const formData = new FormData();
      formData.append('location', location ? `${location.latitude}, ${location.longitude}` : '');
      formData.append('latitude', location?.latitude || '');
      formData.append('longitude', location?.longitude || '');
      
      if (image) {
        formData.append('selfie_base64', image);
        const blob = await fetch(image).then(r => r.blob());
        formData.append('selfie', blob, 'selfie.jpg');
      }

      const response = await attendanceAPI.checkOutFace(formData);
      if (response.data.success) {
        setSuccess(response.data.message);
        const employeeName = response.data.data?.employee_name || user?.name || 'Employee';
        const netMinutes = response.data.data?.attendance?.net_working_time || 0;
        const hours = Math.floor(netMinutes / 60);
        const mins = netMinutes % 60;
        VoiceAnnouncements.logoutMessage(employeeName, hours, mins).catch(() => {});
        loadTodayAttendance();
        setShowCamera(false);
      } else {
        setError(response.data.message);
        VoiceAnnouncements.error(response.data.message).catch(() => {});
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Logout failed';
      setError(errMsg);
      VoiceAnnouncements.error(errMsg).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const handleStartBreak = async (selfieImage) => {
    setLoading(true);
    setError('');
    try {
      const image = selfieImage;
      const formData = new FormData();
      
      if (image) {
        formData.append('selfie_base64', image);
        const blob = await fetch(image).then(r => r.blob());
        formData.append('selfie', blob, 'selfie.jpg');
      }

      const response = await attendanceAPI.breakInFace(formData);
      if (response.data.success) {
        setSuccess(response.data.message);
        const employeeName = user?.name || 'Employee';
        const breakNumber = response.data.data?.break_number || 1;
        VoiceAnnouncements.breakIn(employeeName, breakNumber).catch(() => {});
        loadTodayAttendance();
        loadBreakStatus();
        setShowCamera(false);
      } else {
        setError(response.data.message);
        VoiceAnnouncements.error(response.data.message).catch(() => {});
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to start break';
      setError(errMsg);
      VoiceAnnouncements.error(errMsg).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const handleEndBreak = async (selfieImage) => {
    setLoading(true);
    setError('');
    try {
      const image = selfieImage;
      const formData = new FormData();
      
      if (image) {
        formData.append('selfie_base64', image);
        const blob = await fetch(image).then(r => r.blob());
        formData.append('selfie', blob, 'selfie.jpg');
      }

      const response = await attendanceAPI.breakOutFace(formData);
      if (response.data.success) {
        setSuccess(response.data.message);
        const employeeName = user?.name || 'Employee';
        const durationMinutes = response.data.data?.duration_minutes || 0;
        VoiceAnnouncements.breakOut(employeeName, durationMinutes).catch(() => {});
        loadTodayAttendance();
        loadBreakStatus();
        setShowCamera(false);
      } else {
        setError(response.data.message);
        VoiceAnnouncements.error(response.data.message).catch(() => {});
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to end break';
      setError(errMsg);
      VoiceAnnouncements.error(errMsg).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  const openCamera = (type) => {
    setCameraType(type);
    setShowCamera(true);
    setError('');
    setSuccess('');
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return new Date(time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const isLoggedIn = attendance?.is_logged_in;
  const isOnBreak = attendance?.attendance?.active_break;
  const workingSeconds = getRealTimeWorkingTime();
  const breakSeconds = getRealTimeBreakTime();
  const workingHours = Math.floor(workingSeconds / 60);
  const workingMins = workingSeconds % 60;
  const breakHours = Math.floor(breakSeconds / 60);
  const breakMins = breakSeconds % 60;

  const getStatusColor = () => {
    if (isOnBreak) return '#FFB74D';
    if (isLoggedIn) return '#66BB6A';
    return '#B0BEC5';
  };

  const getStatusText = () => {
    if (isOnBreak) return 'On Break';
    if (isLoggedIn) return 'Working';
    return 'Not Logged In';
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)', 
      minHeight: '100vh', 
      p: { xs: 2, sm: 3 } 
    }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#D4AF37', letterSpacing: 0.5 }}>
              Attendance Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#8AA87D', mt: 0.5 }}>
              Face verification & time tracking
            </Typography>
          </Box>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={isRefreshing ? <CircularProgress size={18} sx={{ color: '#D4AF37' }} /> : <Refresh />}
              onClick={refreshData}
              disabled={isRefreshing}
              sx={{ fontWeight: 600 }}
            >
              Refresh
            </Button>
          </motion.div>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Work Type Selection */}
        {!isLoggedIn && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#D4AF37', letterSpacing: 0.5 }}>
                  Select Work Mode
                </Typography>
                <ToggleButtonGroup
                  value={workType}
                  exclusive
                  onChange={(e, val) => val && setWorkType(val)}
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: '#8AA87D',
                      borderColor: 'rgba(212,175,55,0.3)',
                      py: 1.5,
                      borderRadius: '12px !important',
                      mx: 0.5,
                      transition: 'all 0.3s ease',
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                        color: '#000',
                        fontWeight: 700,
                        '&:hover': { background: 'linear-gradient(135deg, #C5A028 0%, #E6BE33 100%)' },
                      },
                      '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' },
                    },
                  }}
                >
                  <ToggleButton value="wfo">
                    <Business sx={{ mr: 1 }} /> Work From Office
                  </ToggleButton>
                  <ToggleButton value="wfh">
                    <Home sx={{ mr: 1 }} /> Work From Home
                  </ToggleButton>
                </ToggleButtonGroup>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <Grid container spacing={3}>
          {/* Status Overview */}
          <Grid item xs={12}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: `${getStatusColor()}20`,
                        color: getStatusColor(),
                        width: 56, 
                        height: 56,
                        border: `2px solid ${getStatusColor()}`,
                      }}>
                        {isOnBreak ? <Coffee /> : isLoggedIn ? <CheckCircle /> : <Cancel />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#8AA87D' }}>Current Status</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: getStatusColor() }}>
                          {getStatusText()}
                        </Typography>
                        {isLoggedIn && !isOnBreak && (
                          <Typography variant="caption" sx={{ color: '#66BB6A' }}>
                            Active since {formatTime(attendance?.attendance?.login_time)}
                          </Typography>
                        )}
                        {isOnBreak && (
                          <Typography variant="caption" sx={{ color: '#FFB74D' }}>
                            Break since {formatTime(attendance?.attendance?.active_break?.start_time)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                          <Typography variant="body2" sx={{ color: '#8AA87D', mb: 0.5 }}>Working Hours</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#D4AF37' }}>
                            {`${workingHours}h ${workingMins}m`}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                          <Typography variant="body2" sx={{ color: '#8AA87D', mb: 0.5 }}>Break Time</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFB74D' }}>
                            {`${breakHours}h ${breakMins}m`}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                          <Typography variant="body2" sx={{ color: '#8AA87D', mb: 0.5 }}>Face Verification</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: faceVerificationStatus === 'verified' ? '#66BB6A' : '#FFB74D' }}>
                            {faceVerificationStatus === 'verified' ? 'Verified' : faceVerificationStatus === 'pending' ? 'Pending' : 'Not Verified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Today's Attendance */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37', letterSpacing: 0.5 }}>
                  Today's Attendance
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                      <Typography variant="body2" sx={{ color: '#8AA87D' }}>Login Time</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                        {formatTime(attendance?.attendance?.login_time)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                      <Typography variant="body2" sx={{ color: '#8AA87D' }}>Logout Time</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                        {formatTime(attendance?.attendance?.logout_time) || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                      <Typography variant="body2" sx={{ color: '#8AA87D' }}>Work Mode</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#D4AF37' }}>
                        {attendance?.attendance?.work_type === 'wfh' ? '🏠 WFH' : '🏢 WFO'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, background: 'rgba(0,0,0,0.2)', borderRadius: 2, border: '1px solid rgba(212,175,55,0.1)' }}>
                      <Typography variant="body2" sx={{ color: '#8AA87D' }}>Status</Typography>
                      <Chip 
                        label={attendance?.attendance?.status?.toUpperCase() || 'N/A'}
                        sx={{ 
                          mt: 0.5,
                          bgcolor: attendance?.attendance?.status === 'late' ? 'rgba(255,152,0,0.15)' : 'rgba(76,175,80,0.15)',
                          color: attendance?.attendance?.status === 'late' ? '#FFB74D' : '#66BB6A',
                          border: `1px solid ${attendance?.attendance?.status === 'late' ? '#FFB74D' : '#66BB6A'}`,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {location && (
                  <Box sx={{ 
                    mt: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    color: '#8AA87D', 
                    p: 1.5, 
                    background: 'rgba(0,0,0,0.2)', 
                    borderRadius: 2,
                    border: '1px solid rgba(212,175,55,0.08)',
                  }}>
                    <LocationOn fontSize="small" sx={{ color: '#D4AF37' }} />
                    <Typography variant="caption" sx={{ color: '#8AA87D' }}>
                      📍 {location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37', letterSpacing: 0.5 }}>
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  {!isLoggedIn ? (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      startIcon={<CameraAlt />}
                      onClick={() => openCamera('login')}
                      disabled={loading}
                      sx={{ py: 2, fontWeight: 700, fontSize: '1.05rem' }}
                    >
                      Mark Login
                    </Button>
                  ) : (
                    <>
                      {!isOnBreak && (
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          startIcon={<Coffee />}
                          onClick={() => openCamera('break_start')}
                          disabled={!breakStatus?.can_take_break || loading}
                          sx={{ 
                            py: 2, 
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #bda70003 0%, #f5dd00 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #dbb005 0%, #ff990031 100%)' },
                          }}
                        >
                          Start Break
                        </Button>
                      )}

                      {isOnBreak && (
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          startIcon={<Coffee />}
                          onClick={() => openCamera('break_end')}
                          disabled={loading}
                          sx={{ 
                            py: 2, 
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #9b27b00a 0%, #e29300 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #e98503 0%, #9b27b005 100%)' },
                          }}
                        >
                          End Break
                        </Button>
                      )}

                      {!attendance?.is_logged_out && (
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          startIcon={<LogoutIcon />}
                          onClick={() => openCamera('logout')}
                          disabled={loading}
                          sx={{ 
                            py: 2, 
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #ef535000 0%, #C62828 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #ca1414 0%, #ef535000 100%)' },
                          }}
                        >
                          Mark Logout
                        </Button>
                      )}
                    </>
                  )}
                </Stack>

                {breakStatus && isLoggedIn && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    background: 'rgba(0,0,0,0.2)', 
                    borderRadius: 2,
                    border: '1px solid rgba(212,175,55,0.1)',
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#D4AF37' }}>Break Usage</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#8AA87D', display: 'block', mb: 0.5 }}>
                          Short Breaks: {breakStatus.breaks_taken?.short || 0} / {breakStatus.breaks_allowed?.short || 0}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={((breakStatus.breaks_taken?.short || 0) / (breakStatus.breaks_allowed?.short || 1)) * 100}
                          sx={{ height: 6 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#8AA87D', display: 'block', mb: 0.5 }}>
                          Extra Breaks: {breakStatus.breaks_taken?.extra || 0} / {breakStatus.breaks_allowed?.extra || 0}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={((breakStatus.breaks_taken?.extra || 0) / (breakStatus.breaks_allowed?.extra || 1)) * 100}
                          sx={{ 
                            height: 6,
                            '& .MuiLinearProgress-bar': { 
                              background: 'linear-gradient(90deg, #FFB74D, #FF9800)',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                    {breakStatus.requires_break_permission && (
                      <Typography variant="caption" sx={{ color: '#EF5350', display: 'block', mt: 1, fontWeight: 500 }}>
                        ⚠️ Extra breaks require admin/HR permission
                      </Typography>
                    )}
                    {breakStatus.minutes_until_break > 0 && !breakStatus.can_take_break && (
                      <Typography variant="caption" sx={{ color: '#D4AF37', display: 'block', mt: 1 }}>
                        Next break available in {breakStatus.minutes_until_break} minutes
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Breaks History */}
          {attendance?.attendance?.breaks && attendance.attendance.breaks.length > 0 && (
            <Grid item xs={12}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37', letterSpacing: 0.5 }}>
                    Breaks History
                  </Typography>
                  <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Break Type</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>End Time</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attendance.attendance.breaks.map((breakItem, index) => {
                          const startTime = breakItem.start_time ? new Date(breakItem.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-';
                          const endTime = breakItem.end_time ? new Date(breakItem.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : (breakItem.status === 'active' ? 'In Progress' : '-');
                          
                          let breakType = 'Short Break';
                          if (breakItem.break_type === 'extra_break') {
                            breakType = (
                              <>
                                <WarningAmberIcon
                                  sx={{
                                    color: 'warning.main',
                                    mr: 0.5,
                                    verticalAlign: 'middle'
                                  }}
                                />
                                Extra Break {breakItem.break_number || index + 1}
                              </>
                            );
                          } else {
                            breakType = (
                              <>
                                <CoffeeIcon
                                  sx={{
                                    color: '#7ab70176',
                                    mr: 0.5,
                                    verticalAlign: 'middle'
                                  }}
                                />
                                Break {breakItem.break_number || index + 1}
                              </>
                            );
                          }
                          
                          const duration = breakItem.status === 'active' 
                            ? Math.floor((currentTime - new Date(breakItem.start_time)) / 60000)
                            : (breakItem.duration || 0);
                          
                          const isViolation = breakItem.is_violation;
                          
                          return (
                            <TableRow key={breakItem.id || index} hover>
                              <TableCell sx={{ color: '#FFFFFF' }}>{breakType}</TableCell>
                              <TableCell sx={{ color: '#B0BEC5' }}>{startTime}</TableCell>
                              <TableCell sx={{ color: '#B0BEC5' }}>{endTime}</TableCell>
                              <TableCell sx={{ color: isViolation ? '#EF5350' : '#D4AF37', fontWeight: 600 }}>{duration} min</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={breakItem.status === 'active' ? 'Active' : breakItem.status === 'completed' ? 'Completed' : breakItem.status}
                                  sx={{ 
                                    height: 24,
                                    bgcolor: breakItem.status === 'active' ? 'rgba(255,152,0,0.15)' : breakItem.status === 'completed' ? 'rgba(76,175,80,0.15)' : 'rgba(100,100,100,0.15)',
                                    color: breakItem.status === 'active' ? '#FFB74D' : breakItem.status === 'completed' ? '#66BB6A' : '#B0BEC5',
                                    border: `1px solid ${breakItem.status === 'active' ? '#FFB74D' : breakItem.status === 'completed' ? '#66BB6A' : '#666'}`,
                                    fontWeight: 600,
                                  }}
                                />
                                {isViolation && (
                                  <Tooltip title={breakItem.violation_reason}>
                                    <Warning sx={{ ml: 1, fontSize: 16, color: '#EF5350', verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </MotionCard>
            </Grid>
          )}
        </Grid>

        {/* Camera Dialog with Face Detection Overlay */}
        <FaceDetectionCamera
          open={showCamera}
          onClose={() => setShowCamera(false)}
          onCapture={
            cameraType === 'login' ? handleLogin :
            cameraType === 'logout' ? handleLogout :
            cameraType === 'break_start' ? handleStartBreak :
            handleEndBreak
          }
          loading={loading}
          actionLabel={
            cameraType === 'login' ? 'Verify & Login' :
            cameraType === 'logout' ? 'Verify & Logout' :
            cameraType === 'break_start' ? 'Verify & Start Break' :
            'Verify & End Break'
          }
          actionIcon={
            cameraType === 'login' ? <LoginIcon /> :
            cameraType === 'logout' ? <LogoutIcon /> :
            <Coffee />
          }
          actionColor={
            cameraType === 'login' ? 'primary' :
            cameraType === 'logout' ? 'error' :
            'warning'
          }
          employeeName={user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Employee'}
          profilePhotoUrl={(() => {
            // Construct profile photo URL from stored path
            const photo = user?.profile_photo || user?.profile_image;
            if (!photo) return null;
            // If it's already a full URL, use it directly
            if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
            // Remove leading slash and construct full URL
            const cleanPath = photo.replace(/^\//, '');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5076/api';
            const baseUrl = API_URL.replace(/\/api$/, '');
            return `${baseUrl}/${cleanPath}`;
          })()}
        />
      </Box>
    </Box>
  );
};

export default Attendance;
